import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { IAlbum } from "../../../hooks/albums";
import { strings } from "../../../localization/LocalizedStrings";
import styles from "./styles.module.scss";
import { Box, Card, CardActionArea, ListItemText, Stack, useTheme } from "@mui/material";
import { AlertDialog } from "../../AlertDialog";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useGetAIAlbumInsights } from "../../../hooks/openAI/useGetAIAlbumInsights";
import { IOpenAIAlbumInsights } from "../../../hooks/openAI";
import { CustomLoadingIndicator } from "../../CustomLoadingIndicator";
import { SelectChipsListItem } from "../../SelectChipsListItem";
import { format } from "date-fns";
import { Icon, IconType } from "../../../icons";

export const AlbumInsightsListSection: FunctionComponent<IAlbumInsightsListSectionProps> = ({
  album
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const getAlbumInsights = useGetAIAlbumInsights();
  const [insights, setInsights] = useState<IOpenAIAlbumInsights>(undefined);
  const [isExpanded, setIsExpanded] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const currentAlbumUuid = useRef("");
  const fetchAlbumInsights = async (force: boolean) => {
    setIsLoading(true);
    const albumId = album.uuid;
    const { status, body } = await getAlbumInsights(album.uuid, force);
    // Discard result if album changed
    if (albumId !== currentAlbumUuid.current) return;
    if (StatusCodesHelper.isSuccessful(status)) {
      setInsights(body);
      setIsExpanded(true);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    currentAlbumUuid.current = album.uuid;
    setInsights(undefined);
    setIsLoading(false);
    setIsExpanded(false);
  }, [album.uuid]);
  return (
    <div className={styles.mainDiv}>
      <CardActionArea
        className={styles.container}
        onClick={() => {
          if (isLoading) return;
          if (!insights) {
            fetchAlbumInsights(false).then();
          } else {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <Stack direction={"row"} spacing={1} sx={{ width: "100%" }}>
          <Stack>
            <ListItemText
              disableTypography
              className={styles.title}
              secondary={strings.insights_title}
              sx={{ flexGrow: 0 }}
            />
            <p
              className={styles.insightDisclaimer}
              style={{ color: theme.palette.primary.subtitle }}
            >
              {strings.insights_disclaimer}
            </p>
          </Stack>
          <Box flexGrow={1} />
          <Box className={styles.loadingDiv}>
            {isLoading ? (
              <CustomLoadingIndicator />
            ) : (
              <Icon type={isExpanded ? IconType.KeyboardArrowUp : IconType.KeyboardArrowDown} />
            )}
          </Box>
        </Stack>
      </CardActionArea>
      {insights && isExpanded && (
        <Stack className={styles.insightsStack} spacing={2}>
          <p className={styles.insightText} style={{ color: theme.palette.primary.subtitle }}>
            {insights.description}
          </p>
          <p className={styles.insightTitle}>{strings.credits_title}</p>
          <p className={styles.insightText} style={{ color: theme.palette.primary.subtitle }}>
            {insights.credits ?? "--"}
          </p>
          <p className={styles.insightTitle}>{strings.suggested_price_title}</p>
          {insights.suggestedPrice && (
            <Card elevation={2} className={styles.priceCard}>
              <p className={styles.priceText} style={{ fontWeight: "bold" }}>
                {insights.suggestedPrice} USD
              </p>
            </Card>
          )}
          <p className={styles.insightText} style={{ color: theme.palette.primary.subtitle }}>
            {insights.suggestedPriceExplanation ?? "--"}
          </p>
          <SelectChipsListItem
            className={styles.chips}
            title={strings.genres_title}
            chips={insights.musicGenres?.split(",").map((genre, index) => {
              return { id: String(index), name: genre };
            })}
            addDisabled
            removeDisabled
          />
          <SelectChipsListItem
            className={styles.chips}
            title={strings.insights_tags_title}
            chips={insights.tags?.split(",").map((tag, index) => {
              return { id: String(index), name: tag };
            })}
            addDisabled
            removeDisabled
          />
          <p className={styles.insightTitle}>{strings.rarity_title}</p>
          <p className={styles.insightText} style={{ color: theme.palette.primary.subtitle }}>
            {insights.rarity ?? "--"}
          </p>
          <p className={styles.insightDate} style={{ color: theme.palette.primary.subtitle }}>
            {format(insights.createdUnixTimestamp * 1000, "d MMMM yyyy, HH:mm", {
              locale: strings.fnsLocale
            })}
          </p>
        </Stack>
      )}
      {insights && isExpanded && (
        <ToolbarButton
          className={styles.refreshButton}
          iconType={IconType.ModelTraining}
          tooltip={strings.insights_refresh}
          onClick={() => {
            setInsights(undefined);
            fetchAlbumInsights(true).then();
          }}
        />
      )}
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
    </div>
  );
};

export interface IAlbumInsightsListSectionProps {
  album: IAlbum;
}
